import { Modal } from "antd";
import { useEffect, useState } from "react";

const QbdXeroPaymentResultModal = ({
  modalOpen,
  onModalClose,
  paymentData,
  onTryAgain
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(modalOpen);
  }, [modalOpen]);

  const handleClose = () => {
    setIsOpen(false);
    onModalClose();
  };

  const handleTryAgain = () => {
    setIsOpen(false);
    onTryAgain();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={600}
      closeIcon={<div className="text-gray-400 hover:text-gray-600 text-2xl">✕</div>}
      aria-modal="true"
      role="dialog"
    >
      <div className="py-6 px-2">
        {/* Success State */}
        {paymentData.type === 'success' && (
          <>
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Payment Successful!
            </h2>

            {/* Number of Migrations */}
            <div className="bg-green-50 rounded-lg p-4 mb-6 text-center">
              <p className="text-lg text-gray-700">
                You have purchased{' '}
                <span className="font-bold text-green-600">
                  {paymentData.numberOfMigrations} QuickBooks Desktop to Xero{' '}
                  {paymentData.numberOfMigrations === 1 ? 'migration' : 'migrations'}
                </span>
              </p>
            </div>

            {/* Next Steps */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps:</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                    1
                  </div>
                  <p className="text-gray-700 pt-1">
                    Export your QuickBooks Desktop file (.QBW)
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                    2
                  </div>
                  <div className="pt-1">
                    <p className="text-gray-700">
                      Email your .QBW file to:{' '}
                    </p>
                    <a
                      href="mailto:qbd2xero@wowzer.tech"
                      className="text-green-600 font-bold text-lg hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      📧 qbd2xero@wowzer.tech
                    </a>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                    3
                  </div>
                  <p className="text-gray-700 pt-1">
                    Our team will process your request
                  </p>
                </div>
              </div>
            </div>

            {/* Turnaround Time */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⏱️</span>
                <p className="text-blue-900 font-medium">
                  Our team will complete your conversion within{' '}
                  <span className="font-bold">1 business day</span>.
                </p>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Got it!
            </button>
          </>
        )}

        {/* Cancelled State */}
        {paymentData.type === 'cancelled' && (
          <>
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Payment Cancelled
            </h2>

            {/* Message */}
            <div className="bg-yellow-50 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-center">
                {paymentData.message || 'Payment was cancelled. You can try again anytime.'}
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleTryAgain}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="w-full border-2 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}

        {/* Error State */}
        {paymentData.type === 'error' && (
          <>
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Payment Error
            </h2>

            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600 text-center">
                {paymentData.message || 'An error occurred while processing your payment. Please try again.'}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Close
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default QbdXeroPaymentResultModal;
